import React, { useState } from 'react';
import { 
  Box, Container, Typography, Grid, Paper, 
  Chip, Divider, Button, useTheme, useMediaQuery,Avatar  ,
  Dialog, DialogTitle, DialogContent, IconButton,
  Fade
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from './LanguageProvider';
import CloseIcon from '@mui/icons-material/Close';
import GavelIcon from '@mui/icons-material/Gavel';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EmailIcon from '@mui/icons-material/Email';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import ArchiveIcon from '@mui/icons-material/Archive';
import SearchIcon from '@mui/icons-material/Search';
import ChatIcon from '@mui/icons-material/Chat';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningIcon from '@mui/icons-material/Warning';
import HelpIcon from '@mui/icons-material/Help';

export const OurService = () => {
  const { currentLang, direction } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedService, setSelectedService] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const floatingAnimation = {
    y: [0, -15, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Service data with updated names and descriptions
  const servicesData = {
    en: [
      {
        id: 1,
        title: 'Legal Consultations',
        icon: GavelIcon,
        color: '#d4af37',
        shortDesc: 'Expert legal advice without court representation',
        longDesc: 'Providing legal opinions, document analysis, contract review, and clarifying legal procedures for clients. This service is limited to advisory support and does not include litigation, court appearances, client representation, or preparing legal memos on behalf of clients.',
        details: [
          'Legal opinion provision (advisory only)',
          'Document analysis and study assistance',
          'Contract review support',
          'Clarifying legal procedures for clients'
        ],
        note: 'This is an advisory service only. Does NOT include: Litigation, court appearances, client representation, or preparing legal memos on behalf of clients.'
      },
      {
        id: 2,
        title: 'Financial Claims Settlement (Advisory)',
        icon: AccountBalanceIcon,
        color: '#b8942f',
        shortDesc: 'Amicable dispute resolution and settlement proposals',
        longDesc: 'Amicable negotiation assistance between parties, preparing settlement proposals, drafting settlement agreements support, and explaining available legal options. This is an advisory and administrative support service only, not a lawsuit or legal practice.',
        details: [
          'Amicable negotiation assistance between parties',
          'Preparing settlement proposals (administrative support)',
          'Drafting settlement agreements support',
          'Explaining available legal options'
        ],
        note: 'This is an advisory and administrative support service only, not a lawsuit or legal practice.'
      },
      {
        id: 3,
        title: 'Debt Collection Support (Amicable)',
        icon: ReceiptIcon,
        color: '#9e7c2a',
        shortDesc: 'Friendly debt collection and payment scheduling assistance',
        longDesc: 'Administrative communication support with debtor parties, preparing payment notices, assisting with debt scheduling agreements, and administrative follow-up until settlement. Collection support is provided amicably and administratively, not judicially.',
        details: [
          'Administrative communication support with debtor parties',
          'Preparing payment notices',
          'Assisting with debt scheduling agreements',
          'Administrative follow-up support until settlement'
        ],
        note: 'Collection support is provided amicably and administratively, not judicially. No legal representation is provided.'
      },
      {
        id: 4,
        title: 'Legal Notices & Official Correspondence',
        icon: EmailIcon,
        color: '#8b6b1f',
        shortDesc: 'Preparation and management of official correspondence',
        longDesc: 'Administrative preparation of notifications based on client instructions, sending and receiving documents, administrative documentation of correspondence, and submitting requests on electronic systems. This is an administrative support service only.',
        details: [
          'Administrative preparation of notifications',
          'Sending and receiving documents',
          'Administrative documentation of correspondence',
          'Submitting requests on electronic systems'
        ],
        note: 'This is an administrative support service only, not legal or judicial representation.'
      },
      {
        id: 5,
        title: 'Administrative Court Support Services (Non-representational)',
        icon: BusinessIcon,
        color: '#6b4f15',
        shortDesc: 'Electronic file management and document preparation support',
        longDesc: 'Administrative support for opening electronic files based on client documents, extracting judgments, electronically tracking case status, and preparing documents for licensed lawyers. This service does NOT include litigation, representation, memo writing, or court attendance.',
        details: [
          'Administrative support for opening electronic files',
          'Extracting judgments (administrative assistance)',
          'Electronically tracking case status',
          'Preparing documents for licensed lawyers'
        ],
        note: 'Does NOT include: Litigation, representation, memo writing, or court attendance. This is an administrative support service only.'
      }
    ],
    ar: [
      {
        id: 1,
        title: 'الاستشارات القانونية',
        icon: GavelIcon,
        color: '#d4af37',
        shortDesc: 'استشارات قانونية متخصصة بدون تمثيل قضائي',
        longDesc: 'تقديم الاستشارات القانونية، تحليل ودراسة المستندات، مراجعة العقود، وإيضاح الإجراءات القانونية للعملاء. هذه الخدمة استشارية فقط ولا تشمل الترافع أو الحضور أمام المحاكم أو تمثيل العملاء أو إعداد مذكرات قانونية نيابة عنهم.',
        details: [
          'تقديم الاستشارات القانونية',
          'مساعدة في تحليل ودراسة المستندات',
          'دعم في مراجعة العقود',
          'إيضاح الإجراءات القانونية للعملاء'
        ],
        note: 'هذه خدمة استشارية فقط. لا تشمل: الترافع، الحضور أمام المحاكم، تمثيل العملاء، أو إعداد مذكرات قانونية نيابة عنهم.'
      },
      {
        id: 2,
        title: 'تسوية المطالبات المالية (استشاري)',
        icon: AccountBalanceIcon,
        color: '#b8942f',
        shortDesc: 'مساعدة في حل النزاعات بشكل ودي وعرض تسويات',
        longDesc: 'مساعدة في التفاوض الودي بين الأطراف، إعداد مقترحات التسوية، دعم صياغة اتفاقيات التسوية، وبيان الخيارات القانونية المتاحة. الخدمة استشارية ودعم إداري فقط، وليست دعوى قضائية.',
        details: [
          'مساعدة في التفاوض الودي بين الأطراف',
          'إعداد مقترحات التسوية (دعم إداري)',
          'دعم في صياغة اتفاقيات التسوية',
          'بيان الخيارات القانونية المتاحة'
        ],
        note: 'الخدمة استشارية ودعم إداري فقط، وليست دعوى قضائية.'
      },
      {
        id: 3,
        title: 'دعم تحصيل الديون بالطرق الودية',
        icon: ReceiptIcon,
        color: '#9e7c2a',
        shortDesc: 'مساعدة في تحصيل الديون وجدولتها بشكل ودي',
        longDesc: 'دعم في التواصل مع الأطراف المدينة، إعداد إشعارات السداد، المساعدة في الاتفاق على جدولة الديون، والمتابعة الإدارية حتى التسوية. دعم التحصيل يتم بشكل ودي وإداري، وليس قضائياً.',
        details: [
          'دعم في التواصل مع الأطراف المدينة',
          'إعداد إشعارات السداد',
          'المساعدة في الاتفاق على جدولة الديون',
          'المتابعة الإدارية حتى التسوية'
        ],
        note: 'دعم التحصيل يتم بشكل ودي وإداري، وليس قضائياً. لا يوجد تمثيل قانوني.'
      },
      {
        id: 4,
        title: 'التبليغات والمراسلات الرسمية',
        icon: EmailIcon,
        color: '#8b6b1f',
        shortDesc: 'مساعدة في إدارة وتوثيق المراسلات الرسمية',
        longDesc: 'مساعدة إدارية في إعداد الإخطارات بناءً على تعليمات العميل، إرسال واستلام المستندات، التوثيق الإداري للمراسلات، ورفع الطلبات على الأنظمة الإلكترونية. الخدمة دعم إداري فقط.',
        details: [
          'مساعدة إدارية في إعداد الإخطارات',
          'إرسال واستلام المستندات',
          'التوثيق الإداري للمراسلات',
          'رفع الطلبات على الأنظمة الإلكترونية'
        ],
        note: 'هذه خدمة دعم إداري فقط، وليست تمثيلاً قانونياً أو قضائياً.'
      },
      {
        id: 5,
        title: 'خدمات دعم المحاكم الإدارية (بدون تمثيل)',
        icon: BusinessIcon,
        color: '#6b4f15',
        shortDesc: 'دعم في إدارة الملفات إلكترونياً وتجهيز المستندات',
        longDesc: 'دعم إداري لفتح الملفات الإلكترونية بناءً على مستندات العميل، استخراج الأحكام، متابعة حالة الدعوى إلكترونياً، وتجهيز المستندات للمحامين المرخصين. لا تشمل هذه الخدمة الترافع أو التمثيل أو كتابة المذكرات أو الحضور أمام القضاء.',
        details: [
          'دعم إداري لفتح الملفات الإلكترونية',
          'استخراج الأحكام (مساعدة إدارية)',
          'متابعة حالة الدعوى إلكترونياً',
          'تجهيز المستندات للمحامين المرخصين'
        ],
        note: 'لا تشمل: الترافع، التمثيل، كتابة المذكرات، أو الحضور أمام القضاء. هذه خدمة دعم إداري فقط.'
      }
    ]
  };

  // Office services (administrative) with updated descriptions
  const officeServices = {
    en: [
      { icon: DescriptionIcon, text: 'Administrative follow-up on applications and electronic submission support' },
      { icon: AssignmentIcon, text: 'Government transaction follow-up and request submission support according to regulations' },
      { icon: DescriptionIcon, text: 'Form filling and case request assistance based on client-provided data' },
      { icon: ArchiveIcon, text: 'Document preparation, printing, copying, and file formatting support' },
      { icon: VerifiedIcon, text: 'Attestation request follow-up with competent authorities' },
      { icon: ArchiveIcon, text: 'Document archiving and organization services' },
      { icon: SearchIcon, text: 'Case status inquiry and court request updates' },
      { icon: ChatIcon, text: 'Letter and administrative correspondence preparation based on client-provided texts' }
    ],
    ar: [
      { icon: DescriptionIcon, text: 'متابعة إدارية للطلبات ودعم رفعها إلكترونياً' },
      { icon: AssignmentIcon, text: 'متابعة المعاملات الحكومية ودعم تقديم الطلبات وفق الأنظمة' },
      { icon: DescriptionIcon, text: 'مساعدة في تعبئة النماذج وطلبات القضايا بناءً على بيانات العميل' },
      { icon: ArchiveIcon, text: 'دعم في تجهيز المستندات وطباعتها ونسخها وتنسيق الملفات' },
      { icon: VerifiedIcon, text: 'متابعة طلبات التصديق لدى الجهات المختصة' },
      { icon: ArchiveIcon, text: 'خدمات أرشفة وتنظيم المستندات' },
      { icon: SearchIcon, text: 'الاستعلام عن حالة القضايا وطلبات المحاكم' },
      { icon: ChatIcon, text: 'إعداد الخطابات والمراسلات الإدارية بناءً على نصوص العميل' }
    ]
  };

  const currentServices = servicesData[currentLang];
  const currentOfficeServices = officeServices[currentLang];

  const handleOpenDialog = (service) => {
    setSelectedService(service);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTimeout(() => setSelectedService(null), 300);
  };

  return (
    <Box
      id="services"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef5 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated Background Elements */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 45, 0],
          opacity: [0.1, 0.15, 0.1]
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear"
        }}
        style={{
          position: 'absolute',
          top: '5%',
          left: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />

      <motion.div
        animate={floatingAnimation}
        style={{
          position: 'absolute',
          bottom: '10%',
          right: '5%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Section Title */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                  fontWeight: 'bold',
                  color: '#1a1a2e',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                {currentLang === 'ar' ? 'خدماتنا' : 'Our Services'}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                  color: '#d4af37',
                  fontWeight: 600,
                  mb: 2
                }}
              >
                {currentLang === 'ar' ? 'الماريه للإستشارات القانونيه' : 'almaryah Legal Consultancy'}
              </Typography>
              <Divider sx={{ 
                width: '80px', 
                margin: '0 auto', 
                height: '4px', 
                background: 'linear-gradient(90deg, #d4af37, #b8942f)',
                borderRadius: '2px'
              }} />
            </Box>
          </motion.div>

          {/* Main Services Cards */}
          <Grid container spacing={4} sx={{ mb: 8 }}>
            {currentServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Grid item xs={12} sm={6} md={4} key={service.id}>
                  <motion.div
                    variants={itemVariants}
                    whileHover={{ 
                      y: -10,
                      scale: 1.02,
                      transition: { type: "spring", stiffness: 300 }
                    }}
                    style={{ height: '100%' }}
                  >
                    <Paper
                      elevation={3}
                      onClick={() => handleOpenDialog(service)}
                      sx={{
                        p: 3,
                        height: '100%',
                        minHeight: '280px',
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'white',
                        borderRadius: '20px',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        border: '2px solid transparent',
                        '&:hover': {
                          borderColor: service.color,
                          boxShadow: `0 20px 40px ${service.color}20`,
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: `linear-gradient(90deg, ${service.color}, ${service.color}dd)`,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: 'transparent',
                            border: `2px solid ${service.color}`,
                            width: 50,
                            height: 50,
                            mr: direction === 'rtl' ? 0 : 2,
                            ml: direction === 'rtl' ? 2 : 0,
                            '& svg': {
                              color: service.color,
                              fontSize: 25
                            }
                          }}
                        >
                          <Icon />
                        </Avatar>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                            fontWeight: 'bold',
                            color: '#1a1a2e',
                            fontSize: '1rem'
                          }}
                        >
                          {service.title}
                        </Typography>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                          color: '#666',
                          lineHeight: 1.8,
                          flex: 1,
                          mb: 2
                        }}
                      >
                        {service.shortDesc}
                      </Typography>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip
                          label={currentLang === 'ar' ? 'اضغط للمزيد' : 'Click for more'}
                          size="small"
                          sx={{
                            bgcolor: service.color,
                            color: 'white',
                            fontSize: '0.7rem',
                            '&:hover': { opacity: 0.9 }
                          }}
                        />
                        <motion.div
                          animate={{ x: [0, 5, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <Icon sx={{ color: service.color, fontSize: 20 }} />
                        </motion.div>
                      </Box>
                    </Paper>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>

          {/* Office Services Title */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="h4"
              align="center"
              sx={{
                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                fontWeight: 'bold',
                color: '#1a1a2e',
                mb: 4
              }}
            >
              {currentLang === 'ar' ? 'الخدمات المكتبية والإدارية' : 'Office & Administrative Services'}
            </Typography>
          </motion.div>

          {/* Office Services Grid */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {currentOfficeServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    variants={itemVariants}
                    whileHover={{ 
                      y: -5,
                      scale: 1.02,
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                    }}
                    style={{ height: '100%' }}
                  >
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2.5,
                        height: '100%',
                        minHeight: '160px',
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'white',
                        borderRadius: '15px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Icon sx={{ 
                          color: '#d4af37', 
                          fontSize: 24,
                          mr: direction === 'rtl' ? 0 : 1.5,
                          ml: direction === 'rtl' ? 1.5 : 0
                        }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                            color: '#1a1a2e',
                            lineHeight: 1.6,
                            fontWeight: 500
                          }}
                        >
                          {service.text}
                        </Typography>
                      </Box>
                    </Paper>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </motion.div>
      </Container>

      {/* Service Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={400}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: 'white',
            boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
          }
        }}
      >
        {selectedService && (
          <>
            <DialogTitle sx={{ 
              m: 0, 
              p: 3, 
              display: 'flex', 
              alignItems: 'center',
              borderBottom: `3px solid ${selectedService.color}`,
              fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
              fontWeight: 'bold'
            }}>
              <Avatar
                sx={{
                  bgcolor: 'transparent',
                  border: `2px solid ${selectedService.color}`,
                  width: 40,
                  height: 40,
                  mr: direction === 'rtl' ? 0 : 2,
                  ml: direction === 'rtl' ? 2 : 0,
                  '& svg': {
                    color: selectedService.color,
                    fontSize: 20
                  }
                }}
              >
                {React.createElement(selectedService.icon)}
              </Avatar>
              {selectedService.title}
              <IconButton
                onClick={handleCloseDialog}
                sx={{
                  position: 'absolute',
                  right: direction === 'rtl' ? 'auto' : 8,
                  left: direction === 'rtl' ? 8 : 'auto',
                  top: 8,
                  color: '#999'
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ p: 3 }}>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                  color: '#1a1a2e',
                  lineHeight: 1.8,
                  mb: 3
                }}
              >
                {selectedService.longDesc}
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                  fontWeight: 'bold',
                  color: '#1a1a2e',
                  mb: 2
                }}
              >
                {currentLang === 'ar' ? 'تشمل:' : 'Includes:'}
              </Typography>

              <Box sx={{ mb: 3 }}>
                {selectedService.details.map((detail, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1.5
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: selectedService.color,
                        mr: direction === 'rtl' ? 0 : 2,
                        ml: direction === 'rtl' ? 2 : 0
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                        color: '#666'
                      }}
                    >
                      {detail}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Legal Note */}
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 2,
                  background: 'rgba(212,175,55,0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(212,175,55,0.3)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <WarningIcon sx={{ color: selectedService.color, fontSize: 18, mt: 0.2 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                      color: '#666',
                      lineHeight: 1.6,
                      fontSize: '0.75rem'
                    }}
                  >
                    {selectedService.note}
                  </Typography>
                </Box>
              </Paper>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default OurService;